import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { Th, SearchInput } from "../components/Table";
import { TagEditor } from "../components/TagEditor";
import { useClientSort } from "../hooks/useClientSort";
import { userToClipboardText } from "../lib/userClipboard";
import { filterUsers, EMPTY_FILTERS, type UserFilters } from "../lib/userFilters";
import type { UserRow, TagVocabulary } from "../api/types";

// The Users tab (spec-4): one row per signed-up user with the per-user activation
// aggregate, sortable + filterable by any column (dec-8 — the full set loads
// client-side, so it's also agent-legible: every row is in the DOM, no pager). A
// per-row "copy as structured text" feeds an outreach agent (dec-1); tagging an
// owner: is the allotment (dec-2..6).

// Stable accessor map for the per-column client-side sort.
const ACCESSORS: Record<string, (u: UserRow) => string | number | null> = {
  name: (u) => u.name ?? "",
  email: (u) => u.email,
  org: (u) => u.orgs[0]?.orgName ?? "",
  signedUpAt: (u) => u.createdAt,
  specs: (u) => u.specCount,
  decisions: (u) => u.decisionCount,
  acs: (u) => u.acCount,
  mcp: (u) => (u.mcpConnected ? 1 : 0),
};

export function UsersPage() {
  const { user } = useAuth();
  const myEmail = user?.email ?? "";

  const [users, setUsers] = useState<UserRow[]>([]);
  const [vocab, setVocab] = useState<TagVocabulary>({ vocabulary: [], ownerOptions: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Hide-internal defaults ON (dec-9) so the board shows real external signal.
  const [filters, setFilters] = useState<UserFilters>({ ...EMPTY_FILTERS, hideInternal: true });
  const [busy, setBusy] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    const { users } = await api<{ users: UserRow[] }>("/api/users");
    setUsers(users);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ users }, v] = await Promise.all([
          api<{ users: UserRow[] }>("/api/users"),
          api<TagVocabulary>("/api/tags"),
        ]);
        if (!cancelled) {
          setUsers(users);
          setVocab(v);
        }
      } catch {
        if (!cancelled) setError("Failed to load users.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const set = <K extends keyof UserFilters>(key: K, value: UserFilters[K]) =>
    setFilters((f) => ({ ...f, [key]: value }));

  const addTag = useCallback(
    async (entityId: string, scope: string, value: string) => {
      setBusy(true);
      try {
        await api("/api/tags", {
          method: "POST",
          body: JSON.stringify({ entityType: "user", entityId, scope, value }),
        });
        await loadUsers();
        const v = await api<TagVocabulary>("/api/tags");
        setVocab(v);
      } finally {
        setBusy(false);
      }
    },
    [loadUsers],
  );

  const removeTag = useCallback(
    async (entityId: string, scope: string, value: string) => {
      setBusy(true);
      try {
        await api("/api/tags", {
          method: "DELETE",
          body: JSON.stringify({ entityType: "user", entityId, scope, value }),
        });
        await loadUsers();
      } finally {
        setBusy(false);
      }
    },
    [loadUsers],
  );

  const copyRow = async (u: UserRow) => {
    // Per-user clipboard egress only — no server round-trip, no bulk export.
    await navigator.clipboard.writeText(userToClipboardText(u));
    setCopiedId(u.id);
    window.setTimeout(() => setCopiedId((id) => (id === u.id ? null : id)), 1500);
  };

  const filtered = filterUsers(users, filters, myEmail);
  const { sorted, sort, order, toggleSort } = useClientSort(filtered, ACCESSORS, "signedUpAt", "desc");

  if (loading) return <div className="p-8 text-muted">Loading users…</div>;
  if (error) return <div className="p-8 text-danger">{error}</div>;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-heading">Users</h1>
          <p className="text-sm text-muted">
            {filtered.length} of {users.length} users
          </p>
        </div>
        <div className="flex items-center gap-3">
          <SearchInput
            value={filters.search}
            onChange={(v) => set("search", v)}
            placeholder="Search name, email, org, tag…"
          />
          <label className="flex items-center gap-1.5 text-sm text-secondary">
            <input
              type="checkbox"
              data-testid="my-users-toggle"
              checked={filters.myUsers}
              onChange={(e) => set("myUsers", e.target.checked)}
            />
            My users
          </label>
          <label className="flex items-center gap-1.5 text-sm text-secondary">
            <input
              type="checkbox"
              data-testid="hide-internal-toggle"
              checked={filters.hideInternal}
              onChange={(e) => set("hideInternal", e.target.checked)}
            />
            Hide internal
          </label>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-edge">
        <table className="w-full text-sm" data-testid="users-table">
          <thead className="border-b border-edge bg-surface">
            <tr>
              <Th label="Name" field="name" sort={sort} order={order} onSort={toggleSort} />
              <Th label="Email" field="email" sort={sort} order={order} onSort={toggleSort} />
              <Th label="Org(s)" field="org" sort={sort} order={order} onSort={toggleSort} />
              <Th label="Signed up" field="signedUpAt" sort={sort} order={order} onSort={toggleSort} />
              <Th label="Specs" field="specs" sort={sort} order={order} onSort={toggleSort} align="right" />
              <Th label="Decisions" field="decisions" sort={sort} order={order} onSort={toggleSort} align="right" />
              <Th label="ACs" field="acs" sort={sort} order={order} onSort={toggleSort} align="right" />
              <Th label="MCP" field="mcp" sort={sort} order={order} onSort={toggleSort} />
              <Th label="Tags" />
              <Th label="" />
            </tr>
            <tr className="border-b border-edge bg-page/40">
              <FilterCell><FInput testid="filter-name" value={filters.name} onChange={(v) => set("name", v)} /></FilterCell>
              <FilterCell><FInput testid="filter-email" value={filters.email} onChange={(v) => set("email", v)} /></FilterCell>
              <FilterCell><FInput testid="filter-org" value={filters.org} onChange={(v) => set("org", v)} /></FilterCell>
              <FilterCell />
              <FilterCell><FNum testid="filter-minSpecs" value={filters.minSpecs} onChange={(v) => set("minSpecs", v)} /></FilterCell>
              <FilterCell><FNum testid="filter-minDecisions" value={filters.minDecisions} onChange={(v) => set("minDecisions", v)} /></FilterCell>
              <FilterCell><FNum testid="filter-minAcs" value={filters.minAcs} onChange={(v) => set("minAcs", v)} /></FilterCell>
              <FilterCell>
                <select
                  data-testid="filter-mcp"
                  value={filters.mcp}
                  onChange={(e) => set("mcp", e.target.value as UserFilters["mcp"])}
                  className="w-full rounded-md border border-edge bg-surface px-1 py-0.5 text-xs text-primary"
                >
                  <option value="any">any</option>
                  <option value="yes">yes</option>
                  <option value="no">no</option>
                </select>
              </FilterCell>
              <FilterCell><FInput testid="filter-tag" value={filters.tag} onChange={(v) => set("tag", v)} /></FilterCell>
              <FilterCell />
            </tr>
          </thead>
          <tbody>
            {sorted.map((u) => (
              <tr key={u.id} data-testid="user-row" data-user-id={u.id} className="border-b border-edge/60 align-top">
                <td className="px-4 py-3">
                  <Link to={`/users/${u.id}`} className="text-accent hover:underline">
                    {u.name ?? "Unknown"}
                  </Link>
                </td>
                <td className="px-4 py-3 text-secondary" data-testid="user-email">
                  {u.email}
                </td>
                <td className="px-4 py-3 text-secondary">
                  {u.orgs.length ? u.orgs.map((o) => o.orgName).join(", ") : "—"}
                </td>
                <td className="px-4 py-3 text-muted whitespace-nowrap">{u.createdAt.slice(0, 10)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{u.specCount}</td>
                <td className="px-4 py-3 text-right tabular-nums">{u.decisionCount}</td>
                <td className="px-4 py-3 text-right tabular-nums">{u.acCount}</td>
                <td className="px-4 py-3">{u.mcpConnected ? "✓" : "—"}</td>
                <td className="px-4 py-3 min-w-[16rem]">
                  <TagEditor
                    tags={u.tags}
                    vocabulary={vocab.vocabulary}
                    ownerOptions={vocab.ownerOptions}
                    busy={busy}
                    onAdd={(scope, value) => addTag(u.id, scope, value)}
                    onRemove={(scope, value) => removeTag(u.id, scope, value)}
                  />
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    data-testid="copy-row"
                    onClick={() => void copyRow(u)}
                    className="rounded-md border border-edge px-2 py-1 text-xs text-secondary transition-colors hover:bg-card-hover"
                  >
                    {copiedId === u.id ? "Copied" : "Copy"}
                  </button>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-muted">
                  No users match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FilterCell({ children }: { children?: React.ReactNode }) {
  return <th className="px-4 pb-2 font-normal">{children}</th>;
}

function FInput({ testid, value, onChange }: { testid: string; value: string; onChange: (v: string) => void }) {
  return (
    <input
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-md border border-edge bg-surface px-1.5 py-0.5 text-xs text-primary focus:border-accent focus:outline-none"
    />
  );
}

function FNum({ testid, value, onChange }: { testid: string; value: string; onChange: (v: string) => void }) {
  return (
    <input
      type="number"
      min={0}
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="≥"
      className="w-14 rounded-md border border-edge bg-surface px-1.5 py-0.5 text-xs text-primary focus:border-accent focus:outline-none"
    />
  );
}
